import {
    fetchScenario,
    getWidgetDataOrUpdatePage,
    selectBottomSheets,
    selectCurrentPageId,
    selectModalWindows,
    selectPlayerId,
    selectScenarioId,
    selectWidgetByInternalId,
    updateWidgetValueByInternalId,
    useAppDispatch,
    useAppStore,
} from '../store';
import { applyLocalData, copyTextToClipboard, eventBus } from '../foundation';
import {
    ActionActionTypeEnum,
    ActionPlayerTypeNameEnums,
    ActionTypeEnum,
    SAVE_LOCAL_DATA_PREFIX,
    SystemEventTypeEnum,
    TCommonData,
    TPlayerActionData,
    TPlayerActionOption,
    TUseTriggerAction,
    TUseTriggerActionOptions,
    TWAction,
    TWidget,
} from '@libs/types';
import { useCallback, useRef } from 'react';
import { usePlayerActionHandlers, usePortal } from './app';
import { clearAllCaches } from './cache';
import { getGlobalVariables } from './getGlobalVariables';
import { addHistory } from './useNavigationBlockerViaHistory';
import { actionsCall, actionsCallByTypeAndName, isShowLoader } from './utils/triggerActionHelper';

export const useTriggerAction = <TD extends TCommonData>(widget?: TWidget<TD>, isCallForSystemAction?: boolean): TUseTriggerAction<TD> => {
    const { setBottomSheet, setModalWindow } = usePortal();
    const playerActionHandlers = usePlayerActionHandlers();

    const store = useAppStore();
    const redirectURLtoCloseFrameworkRef = useRef<string>(getGlobalVariables('redirectURLtoCloseFramework')?.redirectURLtoCloseFramework);
    const dispatch = useAppDispatch();

    const _isNeedUseKeepalive = useCallback((actions: Array<TWAction>) => {
        return !!actions.find((a) => a.type === ActionTypeEnum.Player && a.name === ActionPlayerTypeNameEnums.closeFramework);
    }, []);

    const _triggerPlayerAction = useCallback(
        (action: TWAction, playerActionOptions?: TPlayerActionOption, value?: TPlayerActionData, widget?: TWidget<TCommonData>) => {
            const state = store.getState();
            const widgetByInternalId = selectWidgetByInternalId(state, action.widgetId ?? '');
            const bottomSheets = selectBottomSheets(state);
            const modalWindows = selectModalWindows(state);

            const finalValue = (value ?? widget?.data?.value) as string;
            const internalWidgetValue = action.widgetId ? widgetByInternalId?.data?.value : finalValue;

            const customHandler = playerActionHandlers?.[action.name];
            if (customHandler) {
                return customHandler(action, playerActionOptions, internalWidgetValue, finalValue);
            }

            // Generic `save_local_data:<key>` action: persist the value under <key> in
            // IndexedDB (read back by other widgets via `useLocalData`) and apply any
            // per-key runtime side effect. The prefix is fixed; the suffix is the key.
            if (action.name?.startsWith(SAVE_LOCAL_DATA_PREFIX)) {
                applyLocalData(action.name.slice(SAVE_LOCAL_DATA_PREFIX.length), finalValue);
                return;
            }

            switch (action.name) {
                case ActionPlayerTypeNameEnums.copy:
                    copyTextToClipboard(playerActionOptions?.textForCopyToClipboard ?? (internalWidgetValue as string));
                    return;
                case ActionPlayerTypeNameEnums.showToast:
                    eventBus.emitGeneral(SystemEventTypeEnum.TOAST_MESSAGE, playerActionOptions?.toastMessage ?? (internalWidgetValue as string));
                    return;
                case ActionPlayerTypeNameEnums.closeFramework: {
                    const swCleanup =
                        'serviceWorker' in navigator
                            ? navigator.serviceWorker.getRegistrations().then((registrations) => Promise.all(registrations.map((r) => r.unregister())))
                            : Promise.resolve();

                    const cleanup = Promise.all([clearAllCaches(), swCleanup]).catch((error) => console.error('Cleanup on closeFramework failed: ', error));

                    if (redirectURLtoCloseFrameworkRef?.current) {
                        cleanup.finally(() => window.location.replace(redirectURLtoCloseFrameworkRef.current));
                    }
                    break;
                }
                case ActionPlayerTypeNameEnums.closeBottomsheet:
                    setBottomSheet(undefined);
                    return;
            }

            if (action.actionType === ActionActionTypeEnum.closeBottomsheet) {
                setBottomSheet(undefined);
                return;
            }

            if (action.widgetId) {
                const { widgetId } = action;
                const modalWindow = modalWindows?.[widgetId];
                const bottomSheet = bottomSheets?.[widgetId];

                if (bottomSheet) {
                    setBottomSheet(widgetId);
                } else if (modalWindow) {
                    setModalWindow(widgetId);
                } else if (finalValue !== undefined) {
                    dispatch(updateWidgetValueByInternalId({ internalId: widgetId, value: finalValue }));
                }
            }
        },
        [store, playerActionHandlers, setBottomSheet, setModalWindow, dispatch],
    );

    const triggerAction = useCallback(
        async (action: TWAction, option?: TUseTriggerActionOptions): Promise<TWidget<TD> | undefined> => {
            if (!process.env.NX_PUBLIC_EMBEDDED_PLAYER && !isCallForSystemAction && action.type !== ActionTypeEnum.Player) {
                addHistory(action);
            }
            const finalOptions: TUseTriggerActionOptions = { ...option, keepalive: _isNeedUseKeepalive(widget?.actions ?? []) };
            const wid = finalOptions.widgetAction ?? widget;
            const showLoader = isShowLoader(action, finalOptions.isShowLoader);

            const state = store.getState();
            const scenarioId = selectScenarioId(state);
            const currentPageId = selectCurrentPageId(state);
            const playerId = selectPlayerId(state);

            if (action.type === ActionTypeEnum.Player) {
                _triggerPlayerAction(action, finalOptions.playerActionOptions, finalOptions.value, wid);
            } else if (action.type === ActionTypeEnum.Widget && wid && !finalOptions.forceScenario) {
                try {
                    return await getWidgetDataOrUpdatePage(
                        action,
                        wid.uid,
                        wid.internalId ?? '',
                        scenarioId,
                        currentPageId,
                        dispatch,
                        finalOptions.isUpdateWidgetByUid ?? true,
                        finalOptions.keepalive,
                        playerId,
                        showLoader,
                    );
                } catch (e) {
                    console.error(`Widget '${wid.id}':${wid.uid} catch error of fetch data:`, e);
                }
            } else {
                dispatch(
                    fetchScenario({
                        scenarioId,
                        currentPageId,
                        action,
                        widgetUid: widget?.uid,
                        keepalive: finalOptions.keepalive,
                        playerId,
                        isShowLoader: showLoader,
                    }),
                );
            }
            return Promise.resolve(undefined);
        },
        [_isNeedUseKeepalive, _triggerPlayerAction, dispatch, isCallForSystemAction, store, widget],
    );

    const triggerActionsByType = useCallback(
        async (type: ActionActionTypeEnum | string, option?: TUseTriggerActionOptions) => {
            const promises = actionsCall(type, (a) => triggerAction(a, option), widget?.actions);
            const results = await Promise.all(promises);
            return results.find((result) => result !== undefined);
        },
        [triggerAction, widget?.actions],
    );

    const triggerActionsByNameAndType = useCallback(
        async (type: ActionActionTypeEnum | string, name: string, option?: TUseTriggerActionOptions) => {
            const promises = actionsCallByTypeAndName(type, name, (a) => triggerAction(a, option), widget?.actions);
            const results = await Promise.all(promises);
            return results.find((result) => result !== undefined);
        },
        [triggerAction, widget?.actions],
    );

    return {
        triggerAction,
        triggerActionsByType,
        triggerActionsByNameAndType,
    };
};
